import React, { useState, useContext, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/Header.css';

// Import icons (you can use react-icons or other icon libraries)
import { 
  FiMenu, 
  FiSearch, 
  FiBell, 
  FiHelpCircle, 
  FiChevronDown,
  FiLogOut,
  FiUser,
  FiSettings
} from 'react-icons/fi';

const Header = ({ toggleSidebar, isSidebarOpen }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  
  // Get page title from location
  const getPageTitle = () => {
    const path = location.pathname;
    
    if (path === '/dashboard') return 'Dashboard';
    if (path.startsWith('/clients')) return 'Clients';
    if (path.startsWith('/templates')) return 'Email Templates';
    if (path.startsWith('/campaigns')) return 'Campaigns';
    if (path.startsWith('/analytics')) return 'Analytics';
    if (path.startsWith('/settings')) return 'Settings';
    if (path.startsWith('/support')) return 'Help & Support';
    
    return 'CRM System';
  };
  
  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setIsNotificationsOpen(false);
  };
  
  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    setIsUserMenuOpen(false);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Searching for:', searchValue);
    // Implement search functionality
  };
  
  // Sample notifications
  const notifications = [
    {
      id: 1,
      title: 'New client added',
      message: 'John Doe was added to your client list',
      time: '15 min ago',
      unread: true
    },
    {
      id: 2,
      title: 'Campaign completed',
      message: 'Your "Spring Promotion" campaign has completed',
      time: '2 hours ago',
      unread: true
    },
    {
      id: 3,
      title: 'System update',
      message: 'CRM system update completed successfully',
      time: 'Yesterday',
      unread: false
    }
  ];
  
  return (
    <header className="app-header">
      <div className="header-left">
        <button className="menu-toggle" onClick={toggleSidebar}>
          <FiMenu />
        </button>
        <h1 className="page-title">{getPageTitle()}</h1>
      </div>
      
      <div className="header-center">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search..."
              className="search-input"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        </form>
      </div>
      
      <div className="header-right">
        <div className="header-actions">
          <div className="header-action" ref={notificationsRef}>
            <button 
              className={`action-button ${isNotificationsOpen ? 'active' : ''}`} 
              onClick={toggleNotifications}
            >
              <FiBell />
              <span className="notification-badge">2</span>
            </button>
            
            {isNotificationsOpen && (
              <div className="dropdown-menu notifications-menu">
                <div className="dropdown-header">
                  <h3>Notifications</h3>
                  <button className="text-button">Mark all as read</button>
                </div>
                
                <div className="notifications-list">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div 
                        key={notification.id} 
                        className={`notification-item ${notification.unread ? 'unread' : ''}`}
                      >
                        <div className="notification-content">
                          <div className="notification-title">{notification.title}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-time">{notification.time}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <p>No notifications yet</p>
                    </div>
                  )}
                </div>
                
                <div className="dropdown-footer">
                  <button className="text-button">View all notifications</button>
                </div>
              </div>
            )}
          </div>
          
          <div className="header-action">
            <button className="action-button">
              <FiHelpCircle />
            </button>
          </div>
        </div>
        
        <div className="user-dropdown" ref={userMenuRef}>
          <button className="user-dropdown-toggle" onClick={toggleUserMenu}>
            <div className="user-avatar">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="user-name d-md-block d-none">{user?.name || 'User'}</span>
            <FiChevronDown className="dropdown-arrow" />
          </button>
          
          {isUserMenuOpen && (
            <div className="dropdown-menu user-menu">
              <div className="dropdown-header user-header">
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="user-info">
                  <div className="user-name">{user?.name || 'User'}</div>
                  <div className="user-email">{user?.email || 'user@example.com'}</div>
                </div>
              </div>
              
              <div className="dropdown-items">
                <a href="/profile" className="dropdown-item">
                  <FiUser className="dropdown-item-icon" />
                  <span>My Profile</span>
                </a>
                <a href="/settings" className="dropdown-item">
                  <FiSettings className="dropdown-item-icon" />
                  <span>Settings</span>
                </a>
              </div>
              
              <div className="dropdown-divider"></div>
              
              <div className="dropdown-items">
                <button onClick={logout} className="dropdown-item text-danger">
                  <FiLogOut className="dropdown-item-icon" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;